import React, { useState, useMemo, useCallback, memo } from "react";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";
import { makeStyles } from "@material-ui/core/styles";
import Dialog from "@material-ui/core/Dialog";
import DialogContent from "@material-ui/core/DialogContent";
import { Search as SearchIcon } from "@material-ui/icons";
import { ThreeDots } from "react-loader-spinner";

import ModalHeader from "components/ModalHeader";
import modalStyle from "assets/jss/material-dashboard-pro-react/modalStyle";
import { grayColor } from "assets/jss/material-dashboard-pro-react";

const useStyles = makeStyles((theme) => ({
  ...modalStyle(theme),
  modal: {
    ...modalStyle(theme).modal,
    maxWidth: "860px",
    width: "90vw",
    marginTop: "50vh !important",
    transform: "translate(0, -50%)",
  },
  dialogContent: {
    padding: "0 20px 16px",
    overflow: "hidden",
    display: "flex",
    flexDirection: "column",
  },
  searchWrapper: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
    borderBottom: `2px solid ${grayColor[8]}`,
    padding: "4px 0",
    marginTop: "8px",
    marginBottom: "10px",
    transition: "border-color 0.2s ease",
  },
  searchIcon: {
    fontSize: "18px",
    color: grayColor[4],
    flexShrink: 0,
  },
  searchInput: {
    border: "none",
    outline: "none",
    fontSize: "13px",
    fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
    fontWeight: 300,
    width: "100%",
    backgroundColor: "transparent",
    color: grayColor[1],
    "&::placeholder": {
      color: grayColor[6],
    },
  },
  chipsRow: {
    display: "flex",
    flexWrap: "wrap",
    gap: "6px",
    marginBottom: "12px",
    flexShrink: 0,
  },
  filterChip: {
    display: "inline-flex",
    alignItems: "center",
    gap: "5px",
    padding: "4px 12px",
    fontSize: "11px",
    fontWeight: 400,
    cursor: "pointer",
    borderRadius: "14px",
    border: `1px solid ${grayColor[8]}`,
    backgroundColor: "transparent",
    color: grayColor[3],
    transition: "all 0.15s ease",
    fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
    userSelect: "none",
    "&:hover": {
      borderColor: grayColor[4],
      backgroundColor: grayColor[12],
    },
  },
  filterChipActive: {
    color: "#fff",
    borderColor: "transparent",
    "&:hover": {
      opacity: 0.9,
      borderColor: "transparent",
    },
  },
  chipCount: {
    fontSize: "10px",
    opacity: 0.8,
  },
  tableWrapper: {
    flex: 1,
    overflow: "auto",
    maxHeight: "50vh",
    borderRadius: "4px",
    border: `1px solid ${grayColor[10]}`,
  },
  table: {
    width: "100%",
    borderCollapse: "collapse",
    fontSize: "12px",
    fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
    fontWeight: 300,
  },
  thead: {
    position: "sticky",
    top: 0,
    zIndex: 1,
    backgroundColor: grayColor[11],
  },
  th: {
    padding: "8px 10px",
    textAlign: "right",
    fontWeight: 400,
    fontSize: "11px",
    color: grayColor[2],
    borderBottom: `1px solid ${grayColor[8]}`,
    whiteSpace: "nowrap",
    "&:first-child": {
      textAlign: "left",
      paddingLeft: "12px",
    },
  },
  tr: {
    transition: "background-color 0.1s ease",
    "&:hover": {
      backgroundColor: grayColor[12],
    },
  },
  td: {
    padding: "6px 10px",
    textAlign: "right",
    borderBottom: `1px solid ${grayColor[10]}`,
    whiteSpace: "nowrap",
    "&:first-child": {
      textAlign: "left",
      paddingLeft: "12px",
    },
  },
  tdName: {
    display: "flex",
    alignItems: "center",
    gap: "6px",
    maxWidth: "260px",
  },
  elementName: {
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  inlineChip: {
    display: "inline-flex",
    alignItems: "center",
    fontSize: "9px",
    fontWeight: 400,
    padding: "2px 7px",
    borderRadius: "10px",
    whiteSpace: "nowrap",
    flexShrink: 0,
  },
  deltaPositive: {
    color: "#2e7d32",
  },
  deltaNegative: {
    color: "#c62828",
  },
  emptyState: {
    textAlign: "center",
    padding: "40px 20px",
    color: grayColor[4],
    fontSize: "13px",
  },
  loadingState: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: "60px",
  },
}));

// Format currency value with magnitude
const fmtValue = (v) => {
  if (v === null || v === undefined) return "-";
  const abs = Math.abs(v);
  if (abs >= 1e9) return `${(v / 1e9).toFixed(1)} B`;
  if (abs >= 1e6) return `${(v / 1e6).toFixed(1)} M`;
  if (abs >= 1e3) return `${(v / 1e3).toFixed(0)} K`;
  return v.toFixed(0);
};

const fmtDelta = (curr, prev) => {
  const diff = curr - prev;
  if (diff === 0) return null;
  const sign = diff >= 0 ? "+" : "";
  return `${sign}${fmtValue(diff)}`;
};

// Chip colors based on BUSINESS MEANING.
// Green = good for business (gained profitability, new clients)
// Red = bad for business (lost profitability, lost clients)
// Gray = neutral (stable)
//
// Transitions are WITHIN THE SAME LEVEL only:
//   gm level: gm_positive ↔ gm_negative
//   sm level: sm_positive ↔ sm_negative
const REASON_COLORS = {
  _stable: grayColor[2],
  // New client = good
  entered_new_in_base: "#2e7d32",
  // GM level: became profitable = good / lost profitability = bad
  entered_from_gm_negative: "#2e7d32",
  entered_from_gm_positive: "#c62828",
  exited_to_gm_negative: "#c62828",
  exited_to_gm_positive: "#2e7d32",
  // SM level: margin improved = good / margin worsened = bad
  entered_from_sm_negative: "#2e7d32",
  entered_from_sm_positive: "#c62828",
  exited_to_sm_negative: "#c62828",
  exited_to_sm_positive: "#2e7d32",
  // Lost client = bad
  exited_left_base: "#c62828",
};

// Special key for stable members
const STABLE_KEY = "_stable";

function CascadeDrilldownDialog({
  open,
  onClose,
  clientColor,
  nodeTitle,
  elements,
  variations,
  loading,
  elementLabel,
  totalEntered,
}) {
  const classes = useStyles();
  const { t } = useTranslation();

  const [searchText, setSearchText] = useState("");
  const [selectedChips, setSelectedChips] = useState(new Set());

  // Reset state when dialog opens with new node
  React.useEffect(() => {
    if (open) {
      setSearchText("");
      setSelectedChips(new Set());
    }
  }, [open, nodeTitle]);

  // Build chip options dynamically from actual data
  const chipOptions = useMemo(() => {
    const opts = [];
    const stableCount = (elements || []).length;

    // Stable members chip (always first)
    opts.push({
      key: STABLE_KEY,
      label: t("alignment_session.cascade.chip_stable"),
      count: stableCount,
      color: REASON_COLORS._stable,
      direction: null,
    });

    // Group variations by direction+reason — each reason is its own chip
    const groups = {};
    for (const v of variations || []) {
      const key = `${v.direction}_${v.reason}`;
      if (!groups[key]) {
        groups[key] = {
          key,
          label: t(`alignment_session.cascade.reason_${v.reason}`),
          count: 0,
          color: REASON_COLORS[key] || grayColor[4],
          direction: v.direction,
        };
      }
      groups[key].count++;
    }

    // Sort: entered first, then exited; within each, by count desc
    const sorted = Object.values(groups).sort((a, b) => {
      if (a.direction !== b.direction) {
        return a.direction === "entered" ? -1 : 1;
      }
      return b.count - a.count;
    });

    opts.push(...sorted);
    return opts;
  }, [elements, variations, t, clientColor]);

  // Handle chip click (toggle; CTRL for multi-select)
  const handleChipClick = useCallback((chipKey, event) => {
    setSelectedChips((prev) => {
      const next = new Set(event.ctrlKey || event.metaKey ? prev : []);
      if (next.has(chipKey)) {
        next.delete(chipKey);
      } else {
        next.add(chipKey);
      }
      return next;
    });
  }, []);

  // Build the combined list for the table, based on selected chips
  const allItems = useMemo(() => {
    // Tag stable elements
    const stableTagged = (elements || []).map((e) => ({
      ...e,
      _chipKey: STABLE_KEY,
    }));
    // Tag variations
    const varTagged = (variations || []).map((v) => ({
      ...v,
      _chipKey: `${v.direction}_${v.reason}`,
    }));
    return [...stableTagged, ...varTagged];
  }, [elements, variations]);

  // Filtered display list
  const displayList = useMemo(() => {
    let list;

    if (selectedChips.size > 0) {
      // Explicit chip selection — show exactly what was requested
      list = allItems.filter((e) => selectedChips.has(e._chipKey));
    } else {
      // Default (no chips selected): show current members only
      // (stable + entered). Exited elements are hidden until their
      // chip is explicitly clicked, because they no longer belong
      // to this node and would show contradictory values.
      list = allItems.filter((e) => e.direction !== "exited");
    }

    // Search filtering
    if (searchText.trim()) {
      const query = searchText.toLowerCase().trim();
      list = list.filter((e) => e.name.toLowerCase().includes(query));
    }

    // Sort: by nr_curr desc (for current members), nr_prev desc (for exited)
    list.sort((a, b) => {
      const aVal = a.direction === "exited" ? a.nr_prev : a.nr_curr;
      const bVal = b.direction === "exited" ? b.nr_prev : b.nr_curr;
      return (bVal || 0) - (aVal || 0);
    });

    return list;
  }, [allItems, selectedChips, searchText]);

  const renderDelta = (curr, prev) => {
    const text = fmtDelta(curr, prev);
    if (!text) return null;
    const diff = curr - prev;
    return (
      <span
        className={diff >= 0 ? classes.deltaPositive : classes.deltaNegative}
        style={{ marginLeft: "4px" }}
      >
        {text}
      </span>
    );
  };

  const renderInlineChip = (el) => {
    const chipKey = el.direction ? `${el.direction}_${el.reason}` : STABLE_KEY;
    const color = REASON_COLORS[chipKey] || grayColor[4];
    const label = el.direction
      ? t(`alignment_session.cascade.reason_${el.reason}`)
      : t("alignment_session.cascade.chip_stable");

    return (
      <span
        className={classes.inlineChip}
        style={{
          backgroundColor: `${color}18`,
          color: color,
        }}
      >
        {label}
      </span>
    );
  };

  return (
    <Dialog
      classes={{ root: classes.modalRoot, paper: classes.modal }}
      open={open}
      onClose={onClose}
      maxWidth={false}
    >
      <ModalHeader
        title={nodeTitle || ""}
        pills={
          !loading && elements
            ? [
                {
                  key: "count",
                  label: `${(
                    (elements || []).length + (totalEntered || 0)
                  ).toLocaleString("pt-BR")} ${elementLabel}`,
                },
              ]
            : []
        }
        onClose={onClose}
        closeLabel={t("common.close")}
        clientColor={clientColor}
      />
      <DialogContent className={classes.dialogContent}>
        {loading ? (
          <div className={classes.loadingState}>
            <ThreeDots width="50px" color={clientColor} />
          </div>
        ) : (
          <>
            {/* Search */}
            <div
              className={classes.searchWrapper}
              style={{ borderBottomColor: clientColor }}
            >
              <SearchIcon
                className={classes.searchIcon}
                style={{ color: clientColor }}
              />
              <input
                className={classes.searchInput}
                type="text"
                placeholder={`${t("common.search").replace("...", "")} ${elementLabel}...`}
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
              />
            </div>

            {/* Chip filters */}
            <div className={classes.chipsRow}>
              {chipOptions.map((chip) => {
                const isActive = selectedChips.has(chip.key);
                return (
                  <button
                    key={chip.key}
                    className={`${classes.filterChip} ${isActive ? classes.filterChipActive : ""}`}
                    style={isActive ? { backgroundColor: chip.color } : {}}
                    onClick={(e) => handleChipClick(chip.key, e)}
                    title="Ctrl+click para selecionar múltiplos"
                  >
                    {chip.label}
                    <span className={classes.chipCount}>({chip.count})</span>
                  </button>
                );
              })}
            </div>

            {/* Table */}
            <div className={classes.tableWrapper}>
              <table className={classes.table}>
                <thead className={classes.thead}>
                  <tr>
                    <th className={classes.th}>
                      {elementLabel?.charAt(0).toUpperCase() +
                        elementLabel?.slice(1)}
                    </th>
                    <th className={classes.th}>
                      {t("alignment_session.cascade.drilldown_revenue")}
                    </th>
                    <th className={classes.th}>
                      {t("alignment_session.cascade.drilldown_gross_margin")}
                    </th>
                    <th className={classes.th}>
                      {t("alignment_session.cascade.drilldown_serve_margin")}
                    </th>
                    <th className={classes.th}>{"Mg S % (pp)"}</th>
                  </tr>
                </thead>
                <tbody>
                  {displayList.length === 0 ? (
                    <tr>
                      <td colSpan={5} className={classes.emptyState}>
                        {searchText
                          ? t("alignment_session.cascade.no_results_search")
                          : t("alignment_session.cascade.no_elements")}
                      </td>
                    </tr>
                  ) : (
                    displayList.map((el, idx) => {
                      // Margin % only makes business sense when revenue > 0.
                      // If revenue is negative (returns/cancellations), showing
                      // a % is misleading (negative/negative = positive).
                      const nrPositive = el.nr_curr > 0;
                      const nrPrevPositive = el.nr_prev > 0;
                      const smPctCurr = nrPositive
                        ? (el.sm_curr / el.nr_curr) * 100
                        : null;
                      const smPctPrev = nrPrevPositive
                        ? (el.sm_prev / el.nr_prev) * 100
                        : null;
                      const smPctDelta =
                        smPctCurr !== null && smPctPrev !== null
                          ? smPctCurr - smPctPrev
                          : null;

                      return (
                        <tr key={idx} className={classes.tr}>
                          <td className={classes.td}>
                            <div className={classes.tdName}>
                              <span className={classes.elementName}>
                                {el.name}
                              </span>
                              {renderInlineChip(el)}
                            </div>
                          </td>
                          <td className={classes.td}>
                            {fmtValue(el.nr_curr)}
                            {renderDelta(el.nr_curr, el.nr_prev)}
                          </td>
                          <td className={classes.td}>
                            {fmtValue(el.gm_curr)}
                            {renderDelta(el.gm_curr, el.gm_prev)}
                          </td>
                          <td className={classes.td}>
                            {fmtValue(el.sm_curr)}
                            {renderDelta(el.sm_curr, el.sm_prev)}
                          </td>
                          <td className={classes.td}>
                            {smPctCurr !== null
                              ? `${smPctCurr.toFixed(1)}%`
                              : "-"}
                            {smPctDelta !== null && smPctDelta !== 0 && (
                              <span
                                className={
                                  smPctDelta >= 0
                                    ? classes.deltaPositive
                                    : classes.deltaNegative
                                }
                                style={{ marginLeft: "4px" }}
                              >
                                {smPctDelta >= 0 ? "+" : ""}
                                {smPctDelta.toFixed(1)}
                              </span>
                            )}
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}

CascadeDrilldownDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  clientColor: PropTypes.string,
  nodeTitle: PropTypes.string,
  elements: PropTypes.array,
  variations: PropTypes.array,
  loading: PropTypes.bool,
  elementLabel: PropTypes.string,
  totalEntered: PropTypes.number,
};

CascadeDrilldownDialog.defaultProps = {
  clientColor: "#1976d2",
  nodeTitle: "",
  elements: null,
  variations: null,
  loading: false,
  elementLabel: "clientes",
  totalEntered: 0,
};

export default memo(CascadeDrilldownDialog);
